import { logD } from "@blacknut/logging/dist";
import { JsSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import React, { PropsWithChildren, useCallback, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

const LOGGING_TAG = "ScrollContext";
const NOP = () => {
  logD(LOGGING_TAG, "NOP");
};
export const ScrollContext = React.createContext<{
  reset: () => void;
  clear: () => void;
  update: (selector: string) => void;
}>({
  reset: NOP,
  clear: NOP,
  update: NOP,
});

const MAP = new Map<
  string,
  number | { overflowing?: string; scrollY: number; selector?: string }
>();

export const ScrollContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const location = useLocation();

  useEffect(() => {
    const listener = () => {
      const MAPTemp = MAP.get(location.pathname);
      MAP.set(location.pathname, {
        scrollY: window.scrollY,
        selector: typeof MAPTemp === "object" ? MAPTemp?.selector : undefined,
        overflowing: typeof MAPTemp === "object" ? MAPTemp?.overflowing : undefined,
      });
    };
    window.addEventListener("scroll", listener);
    return () => {
      window.removeEventListener("scroll", listener);
    };
  }, [location.pathname]);

  useEffect(() => {
    const val = MAP.get(location.pathname);
    logD(LOGGING_TAG, "location changed: %o", location);

    if (typeof val === "number") {
      // Case of window,
      // use a timeout since page content may not have already been displayed
      setTimeout(() => {
        logD(LOGGING_TAG, "Scroll to: %o", val);
        window.scrollTo({
          left: 0,
          top: val,
          behavior: "smooth",
        });
      }, 400);
    } else if (typeof val === "object") {
      // case of overflowing div
      // div can be out of the dom, so use a timeout here efore getting it
      const { overflowing, selector } = val;
      setTimeout(() => {
        const e = !!overflowing ? document.getElementById(overflowing) : window;
        if (e) {
          logD(LOGGING_TAG, "Scroll to: %o", val.scrollY);
          e.scrollTo({
            left: 0,
            top: val.scrollY,
            behavior: !!selector ? "instant" : "smooth",
          });
        }
      }, 400);

      if (selector) {
        setTimeout(() => {
          JsSpatialNavigation.focus(selector, false, undefined);
        }, 500);
      }
    }
  }, [location, location.pathname]);

  const update = useCallback(
    (selector: string) => {
      const MAPTemp = MAP.get(location.pathname);
      MAP.set(location.pathname, {
        scrollY: window.scrollY,
        selector,
        overflowing: typeof MAPTemp === "object" ? MAPTemp?.overflowing : undefined,
      });
    },
    [location.pathname],
  );

  const reset = useCallback(() => {
    logD(LOGGING_TAG, "Reset scroll position for %o", location.pathname);
    MAP.set(location.pathname, 0);
  }, [location.pathname]);

  const clear = useCallback(() => {
    logD(LOGGING_TAG, "Clear saved scroll positions");
    MAP.clear();
  }, []);
  return (
    <ScrollContext.Provider value={{ reset, clear, update }}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScrollContext = () => useContext(ScrollContext);

export const useScrollProvider = ({ overflowing }: { overflowing: string }) => {
  useEffect(() => {
    const e = document.getElementById(overflowing);
    if (e) {
      const listener = () => {
        const MAPTemp = MAP.get(location.pathname);
        MAP.set(location.pathname, {
          overflowing,
          scrollY: e.scrollTop,
          selector: typeof MAPTemp === "object" ? MAPTemp?.selector : undefined,
        });
      };
      e?.addEventListener("scroll", listener);
      return () => {
        e?.removeEventListener("scroll", listener);
      };
    }
    return () => {};
  }, [overflowing]);
};

const ScrollRestorer = (props: { id?: string; scrollKey?: string }) => {
  const context = useContext(ScrollContext);
  const location = useLocation();
  const scrollKey = props.scrollKey || location.pathname;
  useEffect(() => {
    // const e = props.id ? document.getElementById(props.id) : document.getElementById("main");
    // const scrollTop = context.scrollPositions.get(scrollKey);
    // logger.debug("On mount (%o) scroll position is %o, overflowing is %o", scrollKey, scrollTop, e);
    // if (e && e.scrollTo) {
    //     e.scrollTo({ top: scrollTop || 0 });
    // }
    // return () => {
    //     const e = props.id ? document.getElementById(props.id) : document.getElementById("main");
    //     logger.debug(
    //         "On unmount (%o) scroll position is %o, overflowing is %o",
    //         scrollKey,
    //         e ? e.scrollTop : "N/A",
    //         e,
    //     );
    //     if (e) {
    //         context.setScrollPosition(scrollKey, e.scrollTop);
    //     }
    // };
  }, [context, location, props.id, scrollKey]);

  return null;
};

export { ScrollRestorer };
